import {createStore} from '@ngneat/elf';
import {deleteEntities, selectManyByPredicate, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, forelderIdPredikat, hentFraLokallagerEllerNull, leggTilForelderId, oppdaterEntitetMedForelder, registrerEntitetMedForelder, serverFraRequest, serverFraRequestPakketIMeta, slettAlleEntiteterMedForelder, slettAlleEntiteterMedForeldre, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {firstValueFrom, Observable, Subject} from "rxjs";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {sorterLevertSamling} from "felles/ng2/rxjsHjelpere";
import {sorterAvstemning} from "../../modell/avstemningModellHjelpere";
import {AgendaelementId, AvstemningId, AvstemningMedResultatModell, AvstemningMedResultatOgStatuserModell, AvstemningRessursClient, AvstemningsalternativId, AvstemningsstatusModell, ModelloppdateringType, MøtedeltakerId, MøteId, MøteRessursClient, OppdaterAvstemningSpesifikasjon, OppdateringmodellAvstemning, OppdateringmodellMøteOgAvstemninger, OpprettAvstemningSpesifikasjon, StemmeberettigetMøtedeltakerSpesifikasjoner, ÅpneStengeForAvstemningSpesifikasjon} from "../ressurser/generertNg2HttpKlient";
import {DataOgMeta} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {tap} from "rxjs/operators";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import {SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {harTilgangTilSaksdetaljer} from "felles-ng2/modell/sakModellHjelpere";
import {AvstemningsstatusDatatjeneste} from "felles-ng2/tjenester/datalager/AvstemningsstatusDatatjeneste";
import {MøtedeltakereDatatjeneste} from "felles-ng2/tjenester/datalager/MøtedeltakereDatatjeneste";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";

const debug = initDebugger('AvstemningDatatjeneste');

/** Datalager for {@link AvstemningMedResultatModell}. */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class AvstemningDatatjeneste {

    /** Lokalt datalager for {@link AvstemningMedResultatModell} entiteter.
     * Bruker avstemning.sakId som forelder.*/
    readonly avstemningLager = createStore({name: 'avstemning'}, withEntities<AvstemningMedResultatModell, 'id'>());

    /** Holder rede på åpne subscriptions basert på avstemning.id. Benyttes ifbm modelloppdateringer. */
    readonly avstemningAbonnenter = new DatalagerAbonnenter<AvstemningId>('avstemning');

    /** Holder rede på åpne subscriptions basert på sakId (avstemning.sakId). Benyttes ifbm modelloppdateringer. */
    readonly sakAbonnenter = new DatalagerAbonnenter<AgendaelementId>('avstemning.sak');

    private readonly vedNyAvstemningSubject = new Subject<AvstemningMedResultatModell>();

    //disse kan ikke må settes via egne setter pga sirkularitet
    private sakDatatjeneste!: SakDatatjeneste;
    private møteDatatjeneste!: MøteDatatjeneste;

    constructor(public avstemningRessurs: AvstemningRessursClient,
                private møteRessurs: MøteRessursClient,
                private avstemningsstatusDatatjeneste: AvstemningsstatusDatatjeneste,
                private møtedeltakereDatatjeneste: MøtedeltakereDatatjeneste,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private modelloppdateringer: Modelloppdateringer,
                private kontekst: BrukerkontekstTilbyder) {

        this.lyttTilModelloppdateringer();
    }

    /** For intern bruk. Datatjenestene under må settes etter opprettelse av instans pga sirkularitet */
    setDatatjenester(sakDatatjeneste: SakDatatjeneste, møteDatatjeneste: MøteDatatjeneste) {
        this.sakDatatjeneste = sakDatatjeneste;
        this.møteDatatjeneste = møteDatatjeneste;
    }

    //metoder knyttet til AvstemningMedResultatModell

    /** Returnerer angitt avstemning fra lokallager. Returnerer null dersom ikke funnet */
    hentFraLokallager(id: AvstemningId): AvstemningMedResultatModell | null {
        return hentFraLokallagerEllerNull(this.avstemningLager, id);
    }

    /** Observerbar som leverer verdier når {@link #opprett} blir kalt */
    get vedNyAvstemning(): Observable<AvstemningMedResultatModell> {
        return this.vedNyAvstemningSubject;
    }

    /** Returnerer avstemninger tilknyttet angitt sakId */
    finnTilknyttetSakFraLokallager(sakId: AgendaelementId): Observable<AvstemningMedResultatModell[]> {
        return this.sakAbonnenter.observer(sakId,
          this.avstemningLager.pipe(
            selectManyByPredicate(forelderIdPredikat(sakId)),
            sorterLevertSamling(sorterAvstemning)
          ));
    }

    /** Erstatter alle avstemninger knytte til angitt sakId i lokallager */
    erstattAvstemningerTilknyttetSakILokallager(avstemninger: AvstemningMedResultatModell[], sakId: AgendaelementId) {
        erstatteAlleEntiteterMedForelder(this.avstemningLager, avstemninger, sakId);
    }

    /** Sletter alle avstemninger knytte til angitt sakId i lokallager */
    slettAvstemningerTilknyttetSakILokallager(sakId: AgendaelementId) {
        slettAlleEntiteterMedForelder(this.avstemningLager, sakId);
    }

    /** Sletter alle avstemninger knyttet til angitte sakIder i lokallager */
    slettAvstemningerTilknyttetSakerILokallager(sakIder: AgendaelementId[]) {
        slettAlleEntiteterMedForeldre(this.avstemningLager, sakIder);
    }

    /** Oppretter ny avstemning */
    opprett(spesifikasjon: OpprettAvstemningSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AvstemningMedResultatOgStatuserModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.avstemningRessurs.opprett(spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  this.registrerNyAvstemningILager(respons.body!.avstemningMedResultat);
                  this.oppdaterAvstemningsstatusLager(respons.body!.avstemningsstatus);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere avstemning')), 'opprett')
        }));
    }

    oppdater(id: AvstemningId, spesifikasjon: OppdaterAvstemningSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AvstemningMedResultatOgStatuserModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.avstemningRessurs.oppdater(id, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  this.oppdaterAvstemningILager(respons.body!.avstemningMedResultat);
                  this.oppdaterAvstemningsstatusLager(respons.body!.avstemningsstatus);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere avstemning')), 'oppdater')
        }));
    }

    /** Oppdaterer stemmeberettigede for et møte og synkroniserer {@link MøtedeltakereDatatjeneste} med responsen fra server.
     * Sørger også for å oppdatere avstemningsstatus for aktelt møte i {@link AvstemningsstatusDatatjeneste} */
    async oppdaterStemmeberettigede(møteId: MøteId, spesifikasjon: StemmeberettigetMøtedeltakerSpesifikasjoner): Promise<void> {
        const oppdaterteDeltakere = await this.møteRessurs.oppdaterStemmeberettigede(møteId, spesifikasjon)
          .somPromiseVisStandardMeldingVedFeil(i18n('Kunne ikke oppdatere stemmeberettigede'));
        this.møtedeltakereDatatjeneste.erstattDeltakereTilknyttetMøteILokallager(oppdaterteDeltakere.deltakere, møteId);
        await this.avstemningsstatusDatatjeneste.relast(møteId);
    }

    async slett(id: AvstemningId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.avstemningRessurs.slett(id).råRespons()
              .pipe(tap((respons) => {
                  this.avstemningLager.update(deleteEntities(id));
                  this.oppdaterAvstemningsstatusLager(respons.body!);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette avstemning')), 'slett')
        }));
    }

    åpneForAvstemning(id: AvstemningId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AvstemningMedResultatModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.avstemningRessurs.åpneForAvstemning(id).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.avstemningLager, respons.body!, this.forelderIdFraAvstemning(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke åpne for avstemning')), 'åpneForAvstemning')
        }));
    }

    async åpneForAlleAvstemninger(møteId: MøteId, spesifikasjon: ÅpneStengeForAvstemningSpesifikasjon): Promise<void> {
        const oppdatertStatus = await this.møteRessurs.åpneForAvstemning(møteId, spesifikasjon).somPromiseVisMeldingVedFeil(i18n('Kunne ikke åpne for avstemning på møte'))
        this.oppdaterAvstemningsstatusLager(oppdatertStatus);
        //relast alle avstemninger knyttet til møte
        await this.møteDatatjeneste.relast(møteId);
    }

    stengForAvstemning(id: AvstemningId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AvstemningMedResultatModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.avstemningRessurs.stengForAvstemning(id).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.avstemningLager, respons.body!, this.forelderIdFraAvstemning(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke stenge for avstemning')), 'stengForAvstemning')
        }));
    }

    async stengForAlleAvstemninger(møteId: MøteId, spesifikasjon: ÅpneStengeForAvstemningSpesifikasjon): Promise<void> {
        const oppdatertStatus = await this.møteRessurs.stengForAvstemning(møteId, spesifikasjon).somPromiseVisMeldingVedFeil(i18n('Kunne ikke stenge for avstemning på møte'))
        this.oppdaterAvstemningsstatusLager(oppdatertStatus);
        //relast alle avstemninger knyttet til møte
        await this.møteDatatjeneste.relast(møteId);
    }

    avgiStemme(id: AvstemningId, avstemningsalternativId: AvstemningsalternativId | null, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AvstemningMedResultatOgStatuserModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.avstemningRessurs.avgiStemme(id, {avstemningsalternativId}).råRespons()
              .pipe(tap(respons => {
                  this.oppdaterAvstemningILager(respons.body!.avstemningMedResultat);
                  this.oppdaterAvstemningsstatusLager(respons.body!.avstemningsstatus);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke avgi stemme')), 'avgiStemme')
        }));
    }

    nullstillAvgitteStemmerForAvstemning(id: AvstemningId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AvstemningMedResultatOgStatuserModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.avstemningRessurs.nullstillAvgitteStemmer(id).råRespons()
              .pipe(tap(respons => {
                  this.oppdaterAvstemningILager(respons.body!.avstemningMedResultat);
                  this.oppdaterAvstemningsstatusLager(respons.body!.avstemningsstatus);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke nullstille avgitte stemmer')), 'nullstillAvgitteStemmerForAvstemning')
        }));
    }

    nullstillAvgitteStemmerForDeltaker(møteId: MøteId, deltakerId: MøtedeltakerId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<AvstemningsstatusModell>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.møteRessurs.nullstillAvgitteStemmer$DELETE$moete_id_nullstillAvgitteStemmer_deltakerId(møteId, deltakerId).råRespons()
              .pipe(tap(respons => {
                  this.oppdaterAvstemningsstatusLager(respons.body!);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke nullstille avgitte stemmer')), 'nullstillAvgitteStemmerForDeltaker')
        }));
    }

    async nullstillAlleAvgitteStemmer(møteId: MøteId): Promise<void> {
        const oppdatertStatus = await this.møteRessurs.nullstillAvgitteStemmer$DELETE$moete_id_nullstillAvgitteStemmer(møteId).somPromiseVisMeldingVedFeil(i18n('Kunne ikke nullstille alle avgitte stemmer på møte'))
        this.oppdaterAvstemningsstatusLager(oppdatertStatus);
        //relast alle avstemninger knyttet til møte
        await this.møteDatatjeneste.relast(møteId);
    }

    //hjelpemetoder

    private forelderIdFraAvstemning(avstemning: AvstemningMedResultatModell): AgendaelementId {
        return avstemning.sakId!;
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    private registrerNyAvstemningILager(avstemningMedResultat: AvstemningMedResultatModell) {
        registrerEntitetMedForelder(this.avstemningLager, avstemningMedResultat, this.forelderIdFraAvstemning(avstemningMedResultat));
        this.vedNyAvstemningSubject.next(avstemningMedResultat);
    }

    private oppdaterAvstemningILager(avstemningMedResultat: AvstemningMedResultatModell) {
        oppdaterEntitetMedForelder(this.avstemningLager, avstemningMedResultat, this.forelderIdFraAvstemning(avstemningMedResultat));
    }

    private oppdaterAvstemningsstatusLager(avstemningsstatus: AvstemningsstatusModell) {
        this.avstemningsstatusDatatjeneste.erstattILokallager(avstemningsstatus)
    }

    private lyttTilModelloppdateringer() {
        this.lyttTilNyAvstemning();
        this.lyttTilAvstemningOppdatert();
        this.lyttTilAvstemningSlettet();
        this.lyttTilAvstemningÅpnet();
        this.lyttTilAvstemningStengt();
        this.lyttTilMøteAvstemningÅpnet();
        this.lyttTilMøteAvstemningStengt();
    }

    private lyttTilNyAvstemning() {
        this.opprettetEllerOppdatertAvstemningHendelse<OppdateringmodellAvstemning>(ModelloppdateringType.NY_AVSTEMNING)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.sakId)
          .medFilter(modelloppdatering => this.harTilgangTilSaksdetaljer(modelloppdatering.modell))
          .medEtterLagerOppdatert(() => this.visMelding(i18n('Ny avstemning registrert')))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilAvstemningOppdatert() {
        this.opprettetEllerOppdatertAvstemningHendelse<OppdateringmodellAvstemning>(ModelloppdateringType.AVSTEMNING_OPPDATERT)
          .medAbonnentoppslag(this.avstemningAbonnenter, hendelseModell => hendelseModell.avstemningId)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.sakId)
          .medFørLagerOppdatert(() => this.visMelding(i18n('Avstemning har blitt oppdatert')))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilAvstemningSlettet() {
        this.slettetHendelse(ModelloppdateringType.AVSTEMNING_SLETTET)
          .medFørLagerOppdatert(() => this.visMelding(i18n('Avstemning har blitt slettet')))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilAvstemningÅpnet() {
        this.opprettetEllerOppdatertAvstemningHendelse<OppdateringmodellAvstemning>(ModelloppdateringType.AVSTEMNING_ÅPNET)
          .medAbonnentoppslag(this.avstemningAbonnenter, hendelseModell => hendelseModell.avstemningId)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.sakId)
          .medFørLagerOppdatert(() => this.visMelding(i18n('Avstemning åpnet')))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilAvstemningStengt() {
        this.opprettetEllerOppdatertAvstemningHendelse<OppdateringmodellAvstemning>(ModelloppdateringType.AVSTEMNING_STENGT)
          .medAbonnentoppslag(this.avstemningAbonnenter, hendelseModell => hendelseModell.avstemningId)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.sakId)
          .medFørLagerOppdatert(() => this.visMelding(i18n('Avstemning stengt')))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøteAvstemningÅpnet() {
        this.opprettetEllerOppdatertAvstemningerHendelse<OppdateringmodellMøteOgAvstemninger>(ModelloppdateringType.MØTE_AVSTEMNING_ÅPNET)
          .medAbonnentoppslag(this.avstemningAbonnenter, hendelseModell => hendelseModell.avstemninger)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.saker)
          .medFørLagerOppdatert(() => this.visMelding(i18n('Alle avstemning på møte åpnet')))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøteAvstemningStengt() {
        this.opprettetEllerOppdatertAvstemningerHendelse<OppdateringmodellMøteOgAvstemninger>(ModelloppdateringType.MØTE_AVSTEMNING_STENGT)
          .medAbonnentoppslag(this.avstemningAbonnenter, hendelseModell => hendelseModell.avstemninger)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.saker)
          .medFørLagerOppdatert(() => this.visMelding(i18n('Alle avstemning på møte stengt')))
          .oppdaterLagerVedHendelse();
    }

    private opprettetEllerOppdatertAvstemningHendelse<HENDELSE_MODELL_TYPE extends { avstemningId: AvstemningId }>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, AvstemningMedResultatModell>(this.avstemningLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId((hendelseModell: HENDELSE_MODELL_TYPE) => hendelseModell.avstemningId)
          .medEntitetLaster((hendelseModell: HENDELSE_MODELL_TYPE) => this.avstemningRessurs.hent(hendelseModell.avstemningId).råRespons())
          .medForberedLageroppdatering(avstemning => leggTilForelderId(avstemning, this.forelderIdFraAvstemning(avstemning)));
    }

    private opprettetEllerOppdatertAvstemningerHendelse<HENDELSE_MODELL_TYPE extends { moeteId: MøteId; avstemninger: AvstemningId[]; }>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, AvstemningMedResultatModell>(this.avstemningLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId((hendelseModell: HENDELSE_MODELL_TYPE) => hendelseModell.avstemninger)
          .medEntitetLaster((hendelseModell: HENDELSE_MODELL_TYPE) => this.avstemningRessurs.finnAvstemningerKnyttetTilMøte(hendelseModell.moeteId).råRespons())
          .medForberedLageroppdatering(avstemning => leggTilForelderId(avstemning, this.forelderIdFraAvstemning(avstemning)));
    }

    private slettetHendelse(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<AvstemningId, AvstemningMedResultatModell>(this.avstemningLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell);
    }

    private visMelding(melding: string, argumenter = {}) {
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(melding, argumenter));
    }

    private harTilgangTilSaksdetaljer(hendelseModell: { sakId: AgendaelementId }) {
        const sak = this.sakDatatjeneste.sakLager.value.entities[hendelseModell.sakId];
        return erIkkeNil(sak) ? harTilgangTilSaksdetaljer(sak, this.kontekst.autentisertBruker()) : true;
    }
}
